import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../lib/session';

// Menú bajo el nombre, como Google Workspace: configuración de la empresa (admin),
// consola de superadmin (vive en Veta, donde se dan de alta las empresas) y salir.

const VETA_URL = (import.meta.env.VITE_VETA_URL as string | undefined) ?? 'https://horas.ascendix.com.mx';

export function GearIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '·';

export function UserMenu() {
  const { t } = useTranslation();
  const { me, logout } = useSession();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  if (!me) return null;
  const item = 'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-[#3F3D39] hover:bg-paper';

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
        className="flex items-center gap-3 rounded-md px-2 py-1 text-left hover:bg-[#2A2926]">
        <span className="hidden leading-tight sm:block">
          <span className="block">{me.name}</span>
          <span className="block text-xs text-[#C9C7C2]">{me.deskRoleLabel} · {me.tenantName}</span>
        </span>
        <span aria-hidden="true" className="grid h-8 w-8 place-items-center rounded-full bg-[#3A3936] text-xs font-semibold">{initials(me.name)}</span>
      </button>

      {open && (
        <div role="menu" className="absolute right-0 z-40 mt-2 w-72 overflow-hidden rounded-lg border border-line bg-white text-ink shadow-lg">
          <div className="border-b border-[#EFEDE9] px-4 py-3">
            <p className="text-sm font-semibold">{me.name}</p>
            <p className="truncate text-xs text-muted">{me.email}</p>
            <p className="mt-1 text-xs text-muted">{me.deskRoleLabel}{me.platformAdmin ? ' · Plataforma' : ''} · {me.tenantName}</p>
          </div>
          <div className="py-1">
            {me.deskRole === 'admin' && (
              <Link role="menuitem" to="/config" className={item} onClick={() => setOpen(false)}>
                <GearIcon /> {t('shell.settings')} de {me.tenantName}
              </Link>
            )}
            {me.platformAdmin && (
              <a role="menuitem" href={`${VETA_URL}/?view=superadmin`} target="_blank" rel="noopener noreferrer" className={item}
                title="Empresas: se administran en Veta">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                  <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6" />
                </svg>
                <span className="whitespace-nowrap">Consola de superadmin</span>
                <span className="ml-auto text-xs text-muted" title="Se abre en Veta">↗</span>
              </a>
            )}
          </div>
          <div className="border-t border-[#EFEDE9] py-1">
            <button type="button" role="menuitem" onClick={logout} className={item}>{t('shell.logout')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
